import React, { useState, useEffect, useRef } from 'react';
import { Icon, Row, Col, Tooltip } from 'antd';
import moment from 'moment';
import  PropTypes from 'prop-types';
import 'moment-timezone';
import formatter from "../../../../../../utils/formatter";
import { ExchangeFromTable, Footer, FooterText, TableSpan, TotalSpan, FromTableFooter, FromTableFooterCol, RadioInputGroupLabel } from "./styles";
import { ColumnTitleMobile, ColumnValue, UnderlyingContent } from "../../styles";
import RadioTitle from './RadioTitle';
import Context from '../../context';
import { AllocationType as AllocationTypeConstants, ExchangeType as ExchangeTypeConstants, isTIFF } from '../../constants';
import { AlertMessageSelfClose, AlertMessagePrimary } from '../popup';
import Input from '../common/Input';
import DisabledTooltip from './DisabledPoolInfo';
import { AlertContent, Types } from '../../../../../../CommonComponents/ContentMessages';
import { recordError } from "../../../../../../utils/commonUtil";
import { replaceSelect } from "../../../../../../utils/replacer";


export const convertNumber = value => {
    return Number(value).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });
};

export const newArray = (arr) => {
    return arr.map((item) => { return { ...item } });
}

export const isEqualPools = (oldPool, newPool) => {
    if(oldPool === newPool || (oldPool && newPool && oldPool.length !== newPool.length)) {
        return false;
    }
    const diff = newPool.filter((item) => {        
        const pool = oldPool.find((i) => i.poolId === item.poolId);        
        if(pool) {
           return !(pool.dollar === item.dollar)
        } else {            
            return true
        }
    })
    return diff.length === 0;
}

export const usePrevious = (value) => {
    const ref = useRef();    
    useEffect(() => {       
        ref.current = value
    })
    return ref.current
}

const Table = (props) => {
    const { dataSource = [], exchangeType, scheduleType, setDirty: setParentDirty, edit, setError } = props;
    const { exchangeContext={}, setExchangeContext } = React.useContext(Context);
    const { from={} } = exchangeContext;
    const { pools: contextPools, allocationType: contextAllocationType, total: contextTotal = 0 } = from;

    // JUGAAD to get previous state of overall context
    let ctx = window.sessionStorage.getItem('recommend-an-exchange');
    ctx = ctx && JSON.parse(ctx);

    const previousTypes = usePrevious({ exchangeType, scheduleType });

    // This pools is actually whole dataSource and table changes done by user
    const [currentPools, setPools] = useState([]);
    const [total, setTotal] = useState(contextTotal);
    const [allocationType, setAllocationType] = useState(contextAllocationType);
    const [warnChangeAllocationType, setWarnChangeAllocationType] = useState({ display: false });
    const [isDirty, setDirty] = useState(false); // this dirty is for local table changes and reset
    const [hoveredDollar, setHoveredDollar] = useState(false); // The below states are using for highlight the column in mouseover and click on radio butn
    const [hoveredUnit, setHoveredUnit] = useState(false);
    const [hoveredPer, setHoveredPer] = useState(false);
    const [clickedDollar, setClickedDollar] = useState(true);
    const [clickedUnit, setClickedUnit] = useState(false);
    const [clickedPer, setClickedPer] = useState(false);

    // Execute only when dataSource changes
    useEffect(() => {
        if (dataSource && dataSource.length > 0) {
            let tmpPools = newArray(dataSource);
            if (exchangeType === ExchangeTypeConstants.reallocation) {    
                highlightTableColumn('hoverddollar',false); //This is used for remove the default allocation        
                tmpPools = tmpPools.map((item) => {
                    // Don't consider TIFF exchange
                    if(isTIFF(item.poolName)) {
                        return item;
                    }
                    const i = { ...item };
                    i.dollar = i.availableBalance;
                    i.unit = i.availableUnits;
                    i.percentage = 100;
                    return i;
                });                
            } else if (exchangeType === ExchangeTypeConstants.custom) {
                highlightTableColumn('hoverddollar',true);//This is used for highlight the default allocation col 
                setAllocationType('dollar');
                if (contextPools && contextPools.length > 0) { // Only do if context pools are not empty, merge the data.
                    // merge pools (context data) with dataSource (REST API output)
                    // Take all datasource item first, as context pools is subset (change of customer)
                    tmpPools = tmpPools.map((item) => {
                        // Find current investment pool in context pool
                        const changeditem = contextPools.find((i) => i.poolId === item.poolId);
                        if (changeditem) { // if found merge it and return updated object
                            let dollar = changeditem.dollar;
                            let unit = changeditem.unit;
                            let percentage = changeditem.percentage;
                            if (contextAllocationType === AllocationTypeConstants.dollar) {
                                highlightTableColumn('hoverddollar',true);//This is used for highlight the dollar col
                                setAllocationType('dollar');
                                clickedRadioBtnTableColumn('dollar',true);
                                unit =  dollar ? Number((dollar / item.pricePerUnit).toFixed(4)) : '';
                                percentage = dollar ? Number((dollar / item.availableBalance * 100).toFixed(0)) : '';
                            } else if (contextAllocationType === AllocationTypeConstants.unit) {
                                highlightTableColumn('hoverdunit',true);//This is used for highlight the unit col
                                setAllocationType('unit');
                                clickedRadioBtnTableColumn('unit',true);
                                dollar = unit ? Number(unit * item.pricePerUnit).toFixed(2) : '';
                                percentage = unit ? Number((dollar / item.availableBalance * 100).toFixed(0)) : '';
                            } else if (contextAllocationType === AllocationTypeConstants.percentage) {
                                highlightTableColumn('hoverdper',true);//This is used for highlight the percentage col
                                setAllocationType('percentage');
                                clickedRadioBtnTableColumn('percentage',true);
                                dollar = percentage ? Number((item.availableBalance * percentage / 100).toFixed(2)) : '';
                                unit =  percentage ? Number((dollar / item.pricePerUnit).toFixed(4)) : '';
                            }
                            item.availableBalance = changeditem.availableBalance;                           
                            return {
                                ...item,
                                dollar: dollar,
                                unit: unit,
                                percentage: percentage,
                            };
                        } else { // if not found then return the investement pool as is                           
                            return item;
                        }
                    });                                     
                }
            }
            setPools(tmpPools);
            const tmpTotal = tmpPools.reduce((accumulator, item) => accumulator + (Number(item.dollar) || 0), 0)
            setTotal(tmpTotal);
        }
    }, [dataSource]);
    // As Exchange Type or Schedule type is changed reset the table values and update the context accordingly
    useEffect(() => {
        if ((previousTypes && (previousTypes.exchangeType !== exchangeType || previousTypes.scheduleType !== scheduleType))
            && dataSource && dataSource.length > 0) {
            let tmpPools = newArray(dataSource);
            if (exchangeType === ExchangeTypeConstants.reallocation) { 
                highlightTableColumn('hoverddollar',false);//This is used for remove the default allocation 
                tmpPools = tmpPools.map((item) => {
                    // Don't consider TIFF exchange
                    if(isTIFF(item.poolName)) {
                        return item;
                    }
                    const i = { ...item };
                    i.dollar = i.availableBalance;
                    i.unit = i.availableUnits;
                    i.percentage = 100;
                    return i;
                });
            }
            setPools(tmpPools);
            const tmpTotal = tmpPools.reduce((accumulator, item) => accumulator + (item.dollar || 0), 0)
            setTotal(tmpTotal);
            if(exchangeType === ExchangeTypeConstants.custom) {
                highlightTableColumn('hoverddollar',true); //This is used for add the default allocation 
                setAllocationType('dollar');
            }else{ 
                setAllocationType(undefined);
            };
            setError(tmpTotal === 0);
            // Update context directly as all fields are filled automatically and make all radio buttons disabled.
            const tmpContext = { ...exchangeContext };
            tmpContext.from = { ...exchangeContext.from };
            tmpContext.from.scheduleType = scheduleType;
            tmpContext.from.exchangeType = exchangeType;
            tmpContext.from.pools = tmpPools && tmpPools.filter((item) => item.dollar > 0);;
            tmpContext.from.total = tmpTotal;
            tmpContext.from.allocationType = undefined;
            setExchangeContext(tmpContext);
        }
    }, [exchangeType, scheduleType]);

    const updateContext = (pools, total) => {
        // Store the changes in context for future use
        const errorPool = pools.filter((item) => item.error);
        if (errorPool && errorPool.length > 0) {
            setError(true);
        } else {
            setError(false);
        }
        const tmp = pools && pools.filter((item) => item.dollar > 0);
        setParentDirty(!(ctx && isEqualPools(ctx.from.pools, tmp)) && tmp.length > 0);        
        const tmpContext = { ...exchangeContext };
        tmpContext.from = { ...exchangeContext.from, pools: tmp, total, allocationType };
        setExchangeContext(tmpContext);
        setDirty(tmp.length > 0);
    };

    const updateTotal = (pools) => {
        const total = pools.reduce((accumulator, item) => accumulator + (Number(item.dollar) || 0), 0);
        setTotal(total);
        updateContext(pools, total);
    }

    const changeAllocationType = (type) => {
        try {
            clickedRadioBtnTableColumn(type,true);
            toggleHover('',type);
            let tmpDirty = false;
            if (edit) {
                const tmp = currentPools && currentPools.filter((item) => item.dollar > 0);
                tmpDirty = tmp.length > 0
            }            
            if (allocationType && (isDirty || tmpDirty)) { // if allocation type is already existing then only show pop up                
                setWarnChangeAllocationType({ display: true, prevAllocationType: type });
            } else {
                setAllocationType(type);
                resetPoolsData();
            }
        } catch (error) {
            recordError(error);
        }
    }

    const resetPoolsData = () => {
        const tmpPools = newArray(dataSource);
        setPools(tmpPools);
        setTotal(0);
        updateContext(tmpPools, 0);
        setParentDirty(false);
    }

    const handleUpdateAllocationTypeAfterChanges = (isOk) => {
        if (isOk) {
            setAllocationType(warnChangeAllocationType.prevAllocationType);
            resetPoolsData();
        }else {
            highlightTableColumn(allocationType,true);
        }
        setWarnChangeAllocationType({ display: false });
    }

    /** 
     * On Change of percentage, change dollar and unit
     */
    const onChangePercentage = ({ index, percentage }) => {        
        const dataNew = newArray(currentPools);
        dataNew[index].percentage =  Number(percentage);
        dataNew[index].dollar = Number((dataNew[index].availableBalance * dataNew[index].percentage / 100).toFixed(2)) || '';
        // If 100% don't calculate unit from dollar 
        if (dataNew[index].percentage === 100) {
            dataNew[index].unit = dataNew[index].availableUnits;
        } else {
            dataNew[index].unit = Number((dataNew[index].dollar / dataNew[index].pricePerUnit).toFixed(4)) || '';
        }
        if(dataNew[index].availableBalance < dataNew[index].dollar) {
            dataNew[index].error = true;
        } else {
            dataNew[index].error = false;
        }        
        setPools(dataNew);
        updateTotal(dataNew);
        return dataNew;
    };

    /** 
     * On Change of unit, change dollar and percentage
     */
    const onChangeUnit = ({ index, unit }) => {
        const dataNew = newArray(currentPools);
        let unitNum = Number(unit);
        if (dataNew[index].availableUnits === unitNum) {
            dataNew[index].dollar = dataNew[index].availableBalance;
            dataNew[index].percentage = 100;
            dataNew[index].unit = dataNew[index].availableUnits;
        } else {
            dataNew[index].dollar = unitNum ? Number(dataNew[index].pricePerUnit * unitNum).toFixed(2) : '';
            dataNew[index].percentage = Number((dataNew[index].dollar / dataNew[index].availableBalance * 100));
            dataNew[index].unit = unitNum;
        }
        if(dataNew[index].availableUnits < dataNew[index].unit) {
            dataNew[index].error = true;
        } else {
            dataNew[index].error = false;
        }
        setPools(dataNew);
        updateTotal(dataNew);
        return dataNew;
    };

    /** 
     * On Change of dollar, change unit and percentage
     */
    const onChangeDollar = ({ index, dollar }) => {
        const dataNew = newArray(currentPools);
        let dollarNum = Number(dollar);
        if (dataNew[index].availableBalance === dollarNum) {
            dataNew[index].dollar = dataNew[index].availableBalance;
            dataNew[index].percentage = 100;
            dataNew[index].unit = dataNew[index].availableUnits;
        } else {
            dataNew[index].dollar = dollarNum;
            dataNew[index].percentage = Number((dataNew[index].dollar / dataNew[index].availableBalance * 100));
            dataNew[index].unit = Number((dataNew[index].dollar / dataNew[index].pricePerUnit).toFixed(4)) || '';
        }
        if(dataNew[index].availableBalance < dataNew[index].dollar 
            || (Number(dataNew[index].dollar) / Number(dataNew[index].availableBalance) * 100) > 90) {
            dataNew[index].error = true;
        } else {
            dataNew[index].error = false;
        }
        setPools(dataNew);
        updateTotal(dataNew);
        return dataNew;
    }

    const footer = (props) => {
        return (
            <FromTableFooter className="w100">
                <FromTableFooterCol>
                    <FooterText>Total estimated amount:</FooterText>
                    <TableSpan>
                        <TotalSpan>
                            {total ? formatter({ value: total, fieldSuffix: "Dollars" }) : '$0.00'}
                        </TotalSpan>
                    </TableSpan>
                </FromTableFooterCol>
            </FromTableFooter>
        );
    }
    /** 
     * Mouse enter and Mouse leave of unit, change dollar and percentage
     */
   const toggleHover = (msevnt,type) => {
        switch (type) {
            case 'hoverddollar': return msevnt === 'me' ? (highlightTableColumn(type,true)) :  msevnt === 'ml' && !clickedDollar ? (highlightTableColumn(type,false)) :'';
            case 'hoverdunit': return msevnt === 'me' ? (highlightTableColumn(type,true)) : msevnt === 'ml' && !clickedUnit ?  (highlightTableColumn(type,false)) :'';
            case 'hoverdper': return msevnt === 'me' ? (highlightTableColumn(type,true)) : msevnt === 'ml' && !clickedPer ?  (highlightTableColumn(type,false)) :'';
            case 'dollar': return highlightTableColumn(type,true);
            case 'unit': return highlightTableColumn(type,true);
            case 'percentage': return highlightTableColumn(type,true);
            default: return  "";
        }
    }
    /** 
     * Highlight table column based on state
     */
    const highlightTableColumn = (type, isTrueOrFalse) => {
        switch (type) {
            case 'hoverddollar': return isTrueOrFalse ? (setHoveredDollar(isTrueOrFalse), setHoveredUnit(!isTrueOrFalse), setHoveredPer(!isTrueOrFalse)) : (setHoveredDollar(isTrueOrFalse), setHoveredUnit(isTrueOrFalse), setHoveredPer(isTrueOrFalse));
            case 'hoverdunit': return isTrueOrFalse ? (setHoveredUnit(isTrueOrFalse), setHoveredDollar(!isTrueOrFalse), setHoveredPer(!isTrueOrFalse)) : (setHoveredUnit(isTrueOrFalse), setHoveredDollar(isTrueOrFalse), setHoveredPer(isTrueOrFalse));
            case 'hoverdper': return isTrueOrFalse ? (setHoveredPer(isTrueOrFalse),setHoveredUnit(!isTrueOrFalse), setHoveredDollar(!isTrueOrFalse)) : (setHoveredPer(isTrueOrFalse),setHoveredUnit(isTrueOrFalse), setHoveredDollar(isTrueOrFalse));
            case 'dollar': return setHoveredDollar(isTrueOrFalse), setHoveredUnit(!isTrueOrFalse), setHoveredPer(!isTrueOrFalse);
            case 'unit': return setHoveredUnit(isTrueOrFalse), setHoveredDollar(!isTrueOrFalse), setHoveredPer(!isTrueOrFalse);
            case 'percentage': return setHoveredPer(isTrueOrFalse),setHoveredUnit(!isTrueOrFalse), setHoveredDollar(!isTrueOrFalse);
            default: return  "";
        
        }
    }

     /** 
     * Clicked radio button column based on state
     */
      const clickedRadioBtnTableColumn = (type, isTrueOrFalse) => {
        switch (type) {
            case 'dollar': return setClickedDollar(isTrueOrFalse),setClickedUnit(!isTrueOrFalse),setClickedPer(!isTrueOrFalse); //This is used for set state while clicking on the radio btn 
            case 'unit': return setClickedUnit(isTrueOrFalse),setClickedPer(!isTrueOrFalse),setClickedDollar(!isTrueOrFalse);
            case 'percentage': return setClickedPer(isTrueOrFalse),setClickedUnit(!isTrueOrFalse),setClickedDollar(!isTrueOrFalse);
            default: return  "";
        
        }
    }
     /** 
     * Highlight table column based on state after the radio btn click
     */
    const mouseLeaveCheck = () => {
            if(clickedDollar){
                highlightTableColumn('hoverddollar',true);
            }else if(clickedUnit){
                highlightTableColumn('hoverdunit',true);
            }else if(clickedPer){
                highlightTableColumn('hoverdper',true);
            }
    }

    const columns = [
        {
            title: "Investment Options",
            dataIndex: "poolName",
            key: "poolName",
            render: (item) => {
                return <>{replaceSelect(item)}</>
            },
            width: "auto",
            className: "investment-pool-title hide-col-title-in-mobile",
        },
        {
            title: "Available Balance",
            className: "hide-col-title-in-mobile",
            key: "availableBalance",
            render: (item) => {
                let value = "";
                if (item.availableBalance !== 0) {
                    value = formatter({
                        value: item.availableBalance,
                        fieldSuffix: "Dollars"
                    });
                } else {
                    value = "—";
                }
                return <>
                    <ColumnTitleMobile>Available Balance</ColumnTitleMobile>
                    <ColumnValue>{value}</ColumnValue>
                </>
            },
            width: "16%"
        },
        {
            title: "Available Units",
            className: "hide-col-title-in-mobile",
            render: item => {
                let value = formatter({
                    value: item.availableUnits,
                    fieldSuffix: "Units"
                });
                return <>
                    <ColumnTitleMobile>Available Units</ColumnTitleMobile>
                    <ColumnValue>{value}</ColumnValue>
                </>
            },
            key: "availableUnits",
            width: "16%"
        },
        {
            title: "Price per unit",
            className: "hide-col-title-in-mobile",
            render: item => {
                const value = formatter({
                    value: convertNumber(item.pricePerUnit),
                    fieldSuffix: "Dollars"
                });
                const perUpdatedOn = 'Price updated on ' + moment(moment.tz(item.dateOfPricePerUnit, 'America/New_York')).format('MM/DD/YYYY')
                return (
                    <>
                        <ColumnTitleMobile>Price per unit</ColumnTitleMobile>
                        <ColumnValue>
                            <Tooltip placement="bottomLeft" title={perUpdatedOn}>
                                <UnderlyingContent>
                                    <span>{value}</span>{" "}
                                    <Icon type={item.isRise ? 'caret-up' : 'caret-down'} />
                                </UnderlyingContent>
                            </Tooltip>
                            
                        </ColumnValue>
                    </>
                );
            },
            key: "pricePerUnit",
            width: "16%"
        },
        {
            title: curr => <RadioTitle
                text={'Dollars'}
                data-test="changeAllocationRadio"
                unit={AllocationTypeConstants.dollar}
                toolTipText={exchangeType !== ExchangeTypeConstants.reallocation?"Select your allocation preference":""}
                onClick={(type) => changeAllocationType(type)}
                exchangeType={exchangeType}
                checked={allocationType === AllocationTypeConstants.dollar}
                disabled={(!Boolean(exchangeType)) || exchangeType === ExchangeTypeConstants.reallocation}
            />,
            render: (value, record, index) => {
                const overlimit = Number(value.dollar) > Number(record.availableBalance);
                const threshold = (Number(value.dollar) / Number(record.availableBalance) * 100) > 90
                    && (Number(value.dollar) / Number(record.availableBalance) * 100) < 100;
                return (
                    <>
                        {allocationType === AllocationTypeConstants.dollar && <AlertMessageSelfClose
                            title='Alert'
                            visible={threshold}
                        >{`You have requested more than 90% from ${replaceSelect(record.poolName)} pool(s)’ available balance. 
                            Please reduce the amount requested or perform an all shares exchange from this pool(s).`}</AlertMessageSelfClose>}
                        <AlertMessageSelfClose
                            title={
                                <AlertContent
                                    page={"Recommend an Exchange"}
                                    type={[Types.PAGE_ALERTS]}
                                    keyProperty={"Value Exceeds"}
                                    property={"field_header"}
                                    defaultMessage={"Alert"}
                                />
                            }
                            cancelText= {
                                <AlertContent
                                    page={"Recommend an Exchange"}
                                    type={[Types.PAGE_ALERTS]}
                                    keyProperty={"Value Exceeds"}
                                    property={"field_alert_button1_text"}
                                    defaultMessage={"OKAY"}
                                    isButton={true}
                                />
                            }
                            visible={overlimit}
                            value ={value.dollar}
                        >
                            <AlertContent
                                page={"Recommend an Exchange"}
                                type={[Types.PAGE_ALERTS]}
                                keyProperty={"Value Exceeds"}
                                property={"field_description"}
                                html={true}
                                defaultMessage={"Specified value exceeds that of the current position in pool. Please revise"}
                            /> 
                        </AlertMessageSelfClose>
                        <DisabledTooltip visible={allocationType === AllocationTypeConstants.dollar && isTIFF(record.poolName)}>
                            <ColumnTitleMobile>Dollars</ColumnTitleMobile>
                            <Input
                                data-test="dollarFieldInput"
                                disabled={allocationType !== AllocationTypeConstants.dollar || isTIFF(record.poolName)}
                                field='Dollars'
                                regexp={/^\d+\.?\d{0,2}$/}
                                value={value.dollar === undefined ? '' : value.dollar}
                                onChange={(value) => onChangeDollar({ index, record, dollar: value }) }                               
                            />
                        </DisabledTooltip>
                    </>
                );
            },
            key: "dollar",
            className: hoveredDollar ? 'radio-input-col col-dollar active-col' : 'radio-input-col col-dollar',
            onCell:() => {
                return {
                    onMouseEnter: () => {toggleHover('me','hoverddollar');},
                    onMouseLeave: () => {toggleHover('ml','hoverddollar');}
                };
            },
            onHeaderCell:() => {
                return {
                    onMouseEnter: () => {toggleHover('me','hoverddollar');},
                    onMouseLeave: () => {toggleHover('ml','hoverddollar');}
                };
            },
        },
        {
            title: curr => <RadioTitle
                data-test="changeAllocationRadio"
                text={'Units'}
                unit={AllocationTypeConstants.unit}
                toolTipText={exchangeType !== ExchangeTypeConstants.reallocation?"Select your allocation preference":""}
                onClick={(type) => changeAllocationType(type)}
                exchangeType={exchangeType}
                checked={allocationType === AllocationTypeConstants.unit}
                disabled={(!Boolean(exchangeType)) || exchangeType === ExchangeTypeConstants.reallocation}
            />,
            render: (value, record, index) =>{
                return (<>
                    <DisabledTooltip visible={allocationType === AllocationTypeConstants.unit && isTIFF(record.poolName)}>
                        <ColumnTitleMobile>Units</ColumnTitleMobile>
                        <Input
                            data-test="unitFieldInput"
                            disabled={allocationType !== AllocationTypeConstants.unit || isTIFF(record.poolName)}
                            field='Units'
                            regexp={/^\d+\.?\d{0,4}$/}
                            value={value.unit === undefined ? '' : value.unit}
                            onChange={(value) => onChangeUnit({ index, unit: value, record })}                         
                        />
                    </DisabledTooltip>
            </>)},
            key: "unit",
            tooltipTitle: "",
            className: hoveredUnit ? 'radio-input-col col-unit active-col' : 'radio-input-col col-unit',
            onCell:() => {
                return {
                    onMouseEnter: () => {toggleHover('me','hoverdunit');},
                    onMouseLeave: () => {toggleHover('ml','hoverdunit');}
                };
            },
            onHeaderCell:() => {
                return {
                    onMouseEnter: () => {toggleHover('me','hoverdunit');},
                    onMouseLeave: () => {toggleHover('ml','hoverdunit');}
                };
            },
        },
        {
            title: curr => <RadioTitle
                text={'Percentage*'}
                data-test="changeAllocationRadio"
                unit={AllocationTypeConstants.percentage}
                toolTipText={exchangeType !== ExchangeTypeConstants.reallocation?"Select your allocation preference":""}
                onClick={(type) => changeAllocationType(type)}
                exchangeType={exchangeType}
                checked={exchangeType === ExchangeTypeConstants.reallocation ? false :  allocationType === AllocationTypeConstants.percentage}
                disabled={(!Boolean(exchangeType)) || exchangeType === ExchangeTypeConstants.reallocation}
            />,
            render: (value, record, index) => {
                return (<>
                    <DisabledTooltip visible={allocationType === AllocationTypeConstants.percentage && isTIFF(record.poolName)}>
                        <ColumnTitleMobile>Percentage</ColumnTitleMobile>                                            
                        <Input
                            data-test="percentageinput"
                            regexp={/^\d+$/}
                            field='Percentage'
                            value={value.percentage === undefined ? '' :  value.percentage}
                            disabled={(exchangeType === ExchangeTypeConstants.reallocation ? true :  allocationType !== AllocationTypeConstants.percentage) || isTIFF(record.poolName)}
                            onChange={(value) => onChangePercentage({ index, record, percentage: value})}                       
                        />
                    </DisabledTooltip>
                </>);
            },
            key: "percentage",
            tooltipTitle: "",
            className: hoveredPer ? 'radio-input-col col-percentage active-col' : 'radio-input-col col-percentage',
            onCell:() => {
                return {
                    onMouseEnter: () => {toggleHover('me','hoverdper');},
                    onMouseLeave: () => {toggleHover('ml','hoverdper');}
                };
            },
            onHeaderCell:() => {
                return {
                    onMouseEnter: () => {toggleHover('me','hoverdper');},
                    onMouseLeave: () => {toggleHover('ml','hoverdper');}
                };
            },
        }
    ];  

    return (
        <div data-test="exchangeFromTable">
            <Col xs={24} lg={0}>
                {/* this portion is visible only in mobile */}
                <RadioInputGroupLabel>
                    Select your allocation preference
                </RadioInputGroupLabel>
            </Col>
            <AlertMessagePrimary
                data-test="alertMessagePrimary"
                title={
                    <AlertContent
                        page={"Recommend an Exchange"}
                        type={[Types.PAGE_ALERTS]}
                        keyProperty={"Data Loss"}
                        property={"field_header"}
                        defaultMessage={"OKAY"}
                    />
                }
                cancelText={
                    <AlertContent
                        page={"Recommend an Exchange"}
                        type={[Types.PAGE_ALERTS]}
                        keyProperty={"Data Loss"}
                        property={"field_alert_button2_text"}
                        defaultMessage={"No, take me back"}
                        isButton={true}
                    />
                }
                okText={
                    <AlertContent
                        page={"Recommend an Exchange"}
                        type={[Types.PAGE_ALERTS]}
                        keyProperty={"Data Loss"}
                        property={"field_alert_button1_text"}
                        defaultMessage={"CONTINUE"}
                        isButton={true}
                    />
                }
                visible={warnChangeAllocationType.display}
                onCancel={(e) => handleUpdateAllocationTypeAfterChanges(false)}
                onOk={(e) => handleUpdateAllocationTypeAfterChanges(true)}
            >
                <AlertContent
                    page={"Recommend an Exchange"}
                    type={[Types.PAGE_ALERTS]}
                    keyProperty={"Data Loss"}
                    property={"field_description"}
                    html={true}
                    defaultMessage={"Changing the type of Allocation might result in loss of data entered so far. Are you sure you want to continue?"}
                />
            </AlertMessagePrimary>
            <ExchangeFromTable
                data-test="unitTestTable"
                className="exchangeTable"
                footer={footer}
                pagination={false}
                style={{ width: "100%" }}
                columns={columns}
                dataSource={currentPools}
                rowKey={(record) => record.poolId}
                onRow={() => {
                    return {
                      onMouseLeave: () => {mouseLeaveCheck();} // mouse leave row
                    };
                  }}
                onHeaderRow={() => {
                    return {
                      onMouseLeave: () => {mouseLeaveCheck();} // mouse leave row
                    };
                  }}
            />
        </div>
    );
};

Table.propTypes = {
    dataSource: PropTypes.array,
    scheduleType: PropTypes.string,
    exchangeType: PropTypes.string,
    edit: PropTypes.bool
}

export default Table;